import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import useDebounce from '@/common/hooks/useDebounce.ts';
import { SelectionOptionsType } from '@/common/types/SelectionOptions.type.ts';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian';
import convertTimestampToShamsi from '@/common/utils/convertTimestampToShamsi';
import { incidentDetailsCardBodyDataGenerator } from '@/modules/adjusterReportModules/IncidentDetails/data/incidentDetailsCardBodyDataGenerator';
import useIncidentDetailsCauses from '@/modules/adjusterReportModules/IncidentDetails/hooks/incidentDetails/useIncidentDetailsCauses';
import { IncidentDetailsBodyFormType } from '@/modules/adjusterReportModules/IncidentDetails/pages/accidentDetailsBody/accidentDetailsBody.types';
import getAtFaultIdentifierOptionsQuery from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/getAtFaultIdentifierOptionsQuery';
import getBodyDamagedTypesQuery from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/getBodyDamagedTypesQuery';
import getIncidentCardDetailsInfoBody from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/getIncidentCardDetailsInfoBody';
import { IncidentCardDetailsInfoBodyResType } from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/getIncidentCardDetailsInfoBody.types';
import incidentDetailsMutationBody from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/incidentDetailsMutationBody';
import { IncidentDetailsMutationBodyDataTypes } from '@/modules/adjusterReportModules/IncidentDetails/queries/incidentDetails/body/incidentDetailsMutationBody.types';
import { useState } from 'react';
import { SubmitHandler, useFormContext, useWatch } from 'react-hook-form';

const useIncidentDetailsBody = () => {
  const { handleSubmit, control, setValue, formState, reset } =
    useFormContext<IncidentDetailsBodyFormType>();
  const convertToBoolean = (value: string): boolean => {
    return value === 'true';
  };
  const damageTypeValue = useWatch({ control, name: 'bodyDamageType' });
  const [damageType, setDamageType] = useState<string>('');
  const { id } = useParams();
  const debounce = useDebounce();
  const navigate = useNavigate();
  const onSubmit: SubmitHandler<IncidentDetailsBodyFormType> = (data) => {
    const mutatedData = {
      body_damaged_type: data?.bodyDamageType?.value,
      has_destruction: convertToBoolean(data?.isDemolitionOccurred),
      event_cause: data?.eventCause?.value,
      event_happening_date: convertPersianToGregorian(data?.eventDate),
      event_happening_time: data?.eventTime,
      event_happening_site: data?.eventLocation,

      event_happening_province: data?.eventProvince,
      event_happening_county: data?.eventCounty,
      at_fault_identifier: data?.atFaultIdentifier?.value,
      event_happening_description: data?.eventDescription,
    };
    mutateBodyForm(mutatedData);
  };
  const { incidentCausePending, incidentCauseData, onIncidentDetailsChange } =
    useIncidentDetailsCauses(id!);

  const handleDamageTypeOnChange = (value: string) => {
    if (value) {
      debounce(() => {
        setDamageType(value);
      }, 1000);
    } else {
      setDamageType(value);
    }
  };

  const { mutate: mutateBodyForm, isPending: bodyFormMutationPending } =
    useMutation({
      mutationKey: ['incidentDetailsBodyForm', id],
      mutationFn: (formData: IncidentDetailsMutationBodyDataTypes) =>
        incidentDetailsMutationBody({ id, formData }),
      onSuccess: () => navigate(-1),
    });

  const { data: atFaultIdentifier, isPending: atFaultIdentifierPending } =
    useQuery({
      queryKey: ['atFaultIdentifier'],
      queryFn: getAtFaultIdentifierOptionsQuery,
      select: (data): SelectionOptionsType[] =>
        data.map((item) => ({ label: item.name, value: item.id })),
    });

  const { data: damageTypes, isPending: fetchingDamageType } = useQuery({
    queryKey: ['bodyDamageType', damageType],
    queryFn: () => getBodyDamagedTypesQuery({ fa_name: damageType }),
    select: (data) =>
      data.map((item) => ({
        label: item.name.fa_const,
        value: item.id,
        const: item.name?.const,
      })),
  });

  const { data: cardData, isPending: cardDataPending } = useQuery({
    queryKey: ['accidentDetailsBody', id],
    queryFn: () => getIncidentCardDetailsInfoBody({ id }),
    select: (data) => ({
      ...data,
      detailCardItems: incidentDetailsCardBodyDataGenerator(data),
    }),
    meta: {
      onSuccess: (data: IncidentCardDetailsInfoBodyResType) => {
        reset({
          atFaultIdentifier: {
            label: data.at_fault_identifier?.name,
            value: data.at_fault_identifier?.id,
          },
          bodyDamageType: {
            label: data.body_damaged_type?.fa_name,
            value: data.body_damaged_type?.id,
          },
          eventCause: {
            label: data.event_cause?.name,
            value: data.event_cause?.id,
          },
          eventDate: data?.event_happening_date
            ? convertTimestampToShamsi(Number(data?.event_happening_date))
            : '',
          eventTime: data.event_happening_time,
          eventLocation: data.event_happening_site,
          eventLocationPhi: data.event_lat ?? null,
          eventLocationLambda: data.event_lng ?? null,
        });
      },
    },
  });

  return {
    cardData,
    cardDataPending,
    fetchingDamageType,
    handleDamageTypeOnChange,
    damageTypes,
    handleSubmit,
    onSubmit,
    damageTypeValue,
    incidentCausePending,
    incidentCauseData,
    onIncidentDetailsChange,
    atFaultIdentifier,
    atFaultIdentifierPending,
    setValue,
    bodyFormMutationPending,
    formState,
  };
};

export default useIncidentDetailsBody;
